<template>
  <div>
    <!-- Grid -->
    <div class="pa-gantt__grid">
      <template v-for="(units, group) in timeAxis">
        <div
          v-for="unit in units"
          :key="`grid-${group}-${unit}`"
          :style="{ width: gridWidth + 'px' }"
        >
          <v-divider vertical />
        </div>
      </template>
    </div>

    <!-- Today marker -->
    <div
      v-if="todayMarkerPosition !== null"
      class="pa-gantt__marker pa-gantt__marker--today"
      :style="{ left: `${todayMarkerPosition}px` }"
    />

    <!-- Time axis -->
    <div class="pa-gantt__time-axis">
      <div
        v-for="(units, group) in timeAxis"
        :key="`time-group-${group}`"
        class="pa-gantt__time-axis__group"
      >
        <div class="caption">
          <span>{{ group }}</span>
        </div>

        <div
          v-for="unit in units"
          :key="`time-unit-${group}-${unit}`"
          class="pa-gantt__time-axis__unit"
          :style="{ width: gridWidth + 'px' }"
        >
          <span>{{ unit }}</span>
        </div>
      </div>
    </div>

    <v-divider />
  </div>
</template>

<script>
/**
 *  -----------------------------------------
 * |              May 2018   (timeGroup)     |
 *  -----------------------------------------
 * |  1  |  2  |  3  |  4  |  5  (timeUnit)  |
 *  -----------------------------------------
 */
import moment from 'moment'

export default {
  props: {
    ganttEnd: {
      type: Object,
      required: true
    },
    ganttStart: {
      type: Object,
      required: true
    },
    gridWidth: {
      type: Number,
      required: true
    },
    zoomLevel: {
      type: String,
      default: 'month',
      validator (value) {
        return [ 'day', 'week', 'month', 'quarter', 'year' ].includes(value)
      }
    }
  },
  data () {
    return {
      timeAxis: {},
      timeGroupFormat: {
        day: 'MMM YY',
        week: 'MMM YY',
        month: 'YYYY',
        quarter: 'YYYY',
        year: 'YYYY'
      },
      timeUnitFormat: {
        day: 'D',
        week: '[W]W',
        month: 'MMMM',
        quarter: '[Q]Q',
        year: 'YYYY'
      },
      todayMarkerPosition: null
    }
  },
  watch: {
    ganttEnd (val) { this.shouldRedrawAxis({ ganttEnd: val }) },
    ganttStart (val) { this.shouldRedrawAxis({ ganttStart: val }) },
    zoomLevel (val) { this.shouldRedrawAxis({ zoomLevel: val }) }
  },
  mounted () {
    this.calculateTimeAxis()
  },
  methods: {
    calculateTimeAxis () {
      this.timeAxis = {}

      let date = moment(this.ganttStart)

      while (date <= this.ganttEnd) {
        const timeGroup = date.format(this.timeGroupFormat[this.zoomLevel])

        if (!this.timeAxis[timeGroup]) { this.timeAxis[timeGroup] = [] }
        this.timeAxis[timeGroup].push(
          date.format(this.timeUnitFormat[this.zoomLevel])
        )

        date.add(1, `${this.zoomLevel}s`)
      }

      this.placeTodayMarker()
    },
    placeTodayMarker () {
      // Include today mark only if inside chart timeline
      if (moment().isBetween(this.ganttStart, this.ganttEnd)) {
        this.todayMarkerPosition = moment().diff(this.ganttStart, this.zoomLevel, true) * this.gridWidth
      } else {
        this.todayMarkerPosition = null
      }
    },
    shouldRedrawAxis (data) {
      // Function to buffer watch callbacks
      //
      // the update from <gantt> arrives async:
      // first zoomLevel and then ganttStart/End
      // so the redraw func is called 3 times with partially updated values
      // this function assures that all 3 values are received and then
      // triggers the redraw
      if (!this.newData) { this.newData = {} }

      Object.assign(this.newData, data)

      // redraw if received both ganttStart and ganttEnd or if only received zoomLevel
      if ((this.newData.ganttStart && this.newData.ganttEnd) ||
          (!this.newData.ganttStart && !this.newData.ganttEnd && this.newData.zoomLevel)) {
        this.newData = {}
        this.calculateTimeAxis()
      }
    }
  }
}
</script>

<style lang="scss">
@import "~styles/_vars.scss";

.pa-gantt__grid {
  position: absolute;
  left: 0;
  width: 100%;
  height: calc(100% - #{$pa-gantt-task-height});
  top: $pa-gantt-task-height;
  font-size: 0;

  > div {
    height: 100%;
    display: inline-block;

    &:first-child > hr {
      display: none;
    }
  }
}

.pa-gantt__time-axis {
  position: relative;
  height: $pa-gantt-task-height;
  white-space: nowrap;

  &__group,
  &__unit {
    display: inline-block;
    text-align: center;

    span {
      position: sticky;
      left: 4px;
      right: 4px;
    }
  }
}

.pa-gantt__marker {
  width: 2px;
  height: calc(100% - 5px);
  position: absolute;
  background: #3358bd;
  top: 5px;

  &:before {
    content: '';
    background: #3358bd;
    color: white;
    padding: 0 4px;
    border-radius: 2px;
    position: absolute;
    top: 0;
    transform: translateX(-50%);
  }

  &--today {
    background: #e4ac1e;

    &:before {
      background: #e4ac1e;
      color: white;
      height: 1em;
      width: 1em;
      left: 50%;
      border-radius: 50%;
    }
  }
}
</style>
